import { useEffect, useState } from "react";
import { agentApi, ingestionApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";

// Phase B per agent-detail-viewer-v1.md §10 row 14. Paginated/searchable
// list backed by GET /api/agents/:id/knowledge/library (B12). Per-row
// kebab: View · Re-enrich · Soft-delete · Restore · Delete Permanently.
//   View                 → onOpenDrawer with full row payload.
//   Re-enrich            → POST /api/ingestion/documents/{doc_id}/re-enrich
//   Soft-delete          → DELETE /api/ingestion/documents/{doc_id}?mode=soft
//   Restore              → POST   /api/ingestion/documents/{doc_id}/restore
//   Delete Permanently   → DELETE /api/ingestion/documents/{doc_id}?mode=hard
//                          (admin-gated client-side; backend re-checks role)

// Exported for unit testing
export const fmt = (iso) => {
  if (!iso) return "—";
  // Backend may serialize naive datetimes as "2026-04-30T22:00:00" (no
  // TZ suffix). Force UTC so the display matches what the user expects
  // ("uploaded 2 hours ago" not "uploaded 7 hours ago" because of TZ
  // drift). Invalid strings render as "—" instead of "Invalid Date".
  const isoUtc = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z";
  const d = new Date(isoUtc);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const Banner = ({ kind, children, onDismiss }) => {
  const color = kind === "error" ? COLORS.pink : COLORS.green;
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 8,
      border: `1px solid ${color}55`, background: `${color}18`,
      color: COLORS.text, fontSize: 12,
      display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start",
    }}>
      <span>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background: "transparent", border: "none", color: COLORS.textDim,
          cursor: "pointer", fontSize: 14,
        }}>{"✕"}</button>
      )}
    </div>
  );
};

export default function KnowledgeLibraryPanel({ tree, currentUser, onOpenDrawer }) {
  const agentId = tree?.agent?.id;
  const isAdmin = currentUser?.role === "owner" || currentUser?.role === "administrator";
  // B19: developer+ for mutating actions; business_user sees View only.
  const isDeveloper = ["owner", "administrator", "developer"].includes(currentUser?.role);

  const [search, setSearch] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [pages, setPages] = useState([]);          // [[rows...], [rows...]]
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyRow, setBusyRow] = useState(null);   // doc_id of row being mutated
  const [openMenu, setOpenMenu] = useState(null); // id with menu open
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  // Multi-select infrastructure for bulk delete / restore (Commits 5+6).
  // Pre-f953f3f (2026-04-29) the old IngestionView had this on the doc
  // sources tab; the migration to KnowledgeLibraryPanel lost it. Backend
  // endpoints stayed alive (POST /documents/bulk-delete + /bulk-restore).
  const [selectedDocs, setSelectedDocs] = useState(() => new Set());
  const clearSelection = () => setSelectedDocs(new Set());
  const toggleDoc = (docId) => setSelectedDocs(prev => {
    const next = new Set(prev);
    next.has(docId) ? next.delete(docId) : next.add(docId);
    return next;
  });

  const reload = async () => {
    if (!agentId) return;
    setLoading(true);
    setErr(null);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (includeDeleted) params.include_deleted = "true";
      const res = await agentApi.getKnowledgeLibrary(agentId, params);
      const d = res?.data || {};
      setPages([d.rows || []]);
      setNextCursor(d.next_cursor || null);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!agentId || !nextCursor) return;
    setLoading(true);
    try {
      const params = { cursor: nextCursor };
      if (search.trim()) params.search = search.trim();
      if (includeDeleted) params.include_deleted = "true";
      const res = await agentApi.getKnowledgeLibrary(agentId, params);
      const d = res?.data || {};
      setPages(prev => [...prev, d.rows || []]);
      setNextCursor(d.next_cursor || null);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); clearSelection(); }, [agentId, includeDeleted]);

  // Re-enrich / ingestion status pill (Commit 8) — polls the existing
  // GET /api/ingestion/agents/{id}/re-enrich-status endpoint every 5s
  // and surfaces an in-Library count of jobs currently in flight. The
  // canonical detailed surface is the Jobs sub-tab (KnowledgeJobsPanel)
  // which already has per-job stage progress; this pill is a proximate
  // indicator so the user doesn't need to switch tabs to know
  // "something is processing." Hidden when no jobs in flight, hidden
  // entirely for non-admin (the backend endpoint requires admin role).
  const [reEnrichInflight, setReEnrichInflight] = useState(0);
  useEffect(() => {
    if (!agentId || !isAdmin) {
      setReEnrichInflight(0);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await ingestionApi.getReEnrichStatus(agentId);
        if (cancelled) return;
        const inflight = res?.data?.in_flight ?? 0;
        setReEnrichInflight(inflight);
      } catch {
        // Soft-fail — endpoint may be down or auth expired; pill just
        // hides via the 0 fallback.
        if (!cancelled) setReEnrichInflight(0);
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [agentId, isAdmin]);

  const allRows = pages.flat();

  // Selection helpers — derived each render from current state.
  // Live + trash views show different rows so they get separate selections
  // (clearSelection on the toggle handles this).
  const visibleDocIds = allRows.map(r => r.doc_id);
  const allVisibleSelected = visibleDocIds.length > 0
    && visibleDocIds.every(id => selectedDocs.has(id));
  const someVisibleSelected = visibleDocIds.some(id => selectedDocs.has(id));
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      // Deselect only the visible ones; preserve any out-of-page selections.
      setSelectedDocs(prev => {
        const next = new Set(prev);
        visibleDocIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Add all visible to the selection.
      setSelectedDocs(prev => {
        const next = new Set(prev);
        visibleDocIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const action = async (label, fn) => {
    setBusyRow(fn._docId);
    setErr(null);
    try {
      await fn();
      setInfo(`${label} succeeded.`);
      // Refresh current first page so the row reflects the new state.
      await reload();
    } catch (e) {
      setErr(`${label} failed: ${e?.message || String(e)}`);
    } finally {
      setBusyRow(null);
      setOpenMenu(null);
    }
  };

  // Bulk delete (soft) — restores the parity that f953f3f dropped.
  // Backend POST /api/ingestion/documents/bulk-delete (mode=soft) does
  // this in one transaction. Hard-delete intentionally requires the
  // per-doc kebab + explicit confirmation; bulk hard-delete is too
  // dangerous to expose without a separate review pass.
  const [bulkBusy, setBulkBusy] = useState(false);

  // Shared filename-preview helper for confirm dialogs.
  const _previewNames = (ids) => {
    const names = allRows.filter(r => ids.includes(r.doc_id)).map(r => r.filename);
    const preview = names.slice(0, 5).join("\n  • ");
    const more = names.length > 5 ? `\n  …and ${names.length - 5} more` : "";
    return `  • ${preview}${more}`;
  };

  const handleBulkSoftDelete = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) return;
    if (!confirm(
      `Soft-delete ${ids.length} document${ids.length === 1 ? "" : "s"}?\n\n${_previewNames(ids)}\n\n` +
      `Chunks become invisible to retrieval. Restore is reversible for 30 days.`
    )) return;

    setBulkBusy(true);
    setErr(null);
    try {
      await ingestionApi.bulkDeleteDocuments(ids, "soft", "viewer-bulk-action", agentId);
      setInfo(`Soft-deleted ${ids.length} document${ids.length === 1 ? "" : "s"}.`);
      clearSelection();
      await reload();
    } catch (e) {
      setErr(`Bulk delete failed: ${e?.message || String(e)}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // Re-enrich all — agent-scope bulk re-processing (Commit 7).
  // Used after upgrading domain template / changing chunk size /
  // switching embedding model. Per-doc re-enrich still works via
  // kebab; this is the "do them all" shortcut. Admin-gated client-side
  // because it can incur material LLM costs; backend re-checks role
  // AND requires explicit cost_acknowledged=true (we always set it
  // true at this surface because the modal forces a checkbox first).
  const [reEnrichOpen, setReEnrichOpen] = useState(false);
  const [reEnrichBusy, setReEnrichBusy] = useState(false);
  const [reEnrichStages, setReEnrichStages] = useState(["enriching"]);
  const [reEnrichOnlyFailed, setReEnrichOnlyFailed] = useState(true);
  const [reEnrichCostAck, setReEnrichCostAck] = useState(false);

  const RE_ENRICH_STAGE_OPTIONS = [
    { id: "enriching",      label: "Enriching (metadata + summary)" },
    { id: "graph_extract",  label: "Graph extraction (entities + relations)" },
  ];

  const toggleReEnrichStage = (stageId) => {
    setReEnrichStages(prev =>
      prev.includes(stageId)
        ? prev.filter(s => s !== stageId)
        : [...prev, stageId]
    );
  };

  const closeReEnrichModal = () => {
    setReEnrichOpen(false);
    setReEnrichStages(["enriching"]);
    setReEnrichOnlyFailed(true);
    setReEnrichCostAck(false);
  };

  const submitReEnrichAll = async () => {
    if (!agentId) return;
    if (reEnrichStages.length === 0) return;  // button disabled — sanity
    if (!reEnrichCostAck) return;             // button disabled — sanity
    setReEnrichBusy(true);
    setErr(null);
    try {
      const res = await ingestionApi.reEnrichAllAgent(agentId, {
        stages: reEnrichStages,
        only_failed: reEnrichOnlyFailed,
        cost_acknowledged: true,
      });
      const requeued = res?.data?.requeued ?? res?.requeued ?? "?";
      setInfo(`Re-enrich-all queued: ${requeued} document${requeued === 1 ? "" : "s"} re-processing. Watch the Jobs tab.`);
      closeReEnrichModal();
      // Don't reload() — Library list doesn't change (status is shown in Jobs tab).
    } catch (e) {
      setErr(`Re-enrich-all failed: ${e?.message || String(e)}`);
    } finally {
      setReEnrichBusy(false);
    }
  };

  // Bulk restore — only meaningful in trash view (includeDeleted=true).
  // Backend POST /api/ingestion/documents/bulk-restore reverses the
  // soft-delete and the docs return to live retrieval. Lower-risk than
  // bulk delete so no filename preview — just a count confirmation.
  const handleBulkRestore = async () => {
    const ids = Array.from(selectedDocs);
    if (ids.length === 0) return;
    if (!confirm(
      `Restore ${ids.length} document${ids.length === 1 ? "" : "s"} from trash?\n\n${_previewNames(ids)}\n\n` +
      `Chunks become visible to retrieval again.`
    )) return;

    setBulkBusy(true);
    setErr(null);
    try {
      await ingestionApi.bulkRestoreDocuments(ids, agentId);
      setInfo(`Restored ${ids.length} document${ids.length === 1 ? "" : "s"}.`);
      clearSelection();
      await reload();
    } catch (e) {
      setErr(`Bulk restore failed: ${e?.message || String(e)}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // agentId scoping prevents the API from mutating a different agent's
  // row when the same doc_id (deterministic filename hash) exists across
  // multiple agents in the tenant.
  const onReEnrich   = (r) => action("Re-enrich",       Object.assign(() => ingestionApi.reEnrichDocument(r.doc_id, ["enriching"], agentId),    { _docId: r.doc_id }));
  const onSoftDelete = (r) => {
    if (!confirm(`Soft-delete "${r.filename}"?\n\nChunks become invisible to retrieval. Restore is reversible for 30 days.`)) return;
    action("Soft-delete", Object.assign(() => ingestionApi.softDeleteDocument(r.doc_id, "viewer-action", agentId),                    { _docId: r.doc_id }));
  };
  const onRestore    = (r) => action("Restore",         Object.assign(() => ingestionApi.restoreDocument(r.doc_id, agentId),                   { _docId: r.doc_id }));
  const onHardDelete = (r) => {
    if (!isAdmin) return;
    if (!confirm(`Permanently delete "${r.filename}"?\n\nThis runs the 7-store cascade (vector + KG + ES + metadata + minio + ingestion job + custom_fields). Cannot be undone.`)) return;
    action("Delete permanently", Object.assign(() => ingestionApi.hardDeleteDocument(r.doc_id, "viewer-action", agentId),             { _docId: r.doc_id }));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {info && <Banner kind="success" onDismiss={() => setInfo(null)}>{info}</Banner>}
      {err && <Banner kind="error" onDismiss={() => setErr(null)}>{err}</Banner>}

      {/* In-flight ingestion status pill — admin-only, polls every 5s. */}
      {isAdmin && reEnrichInflight > 0 && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          alignSelf: "flex-start",
          padding: "4px 12px", borderRadius: 999,
          fontSize: 11, fontWeight: 600,
          background: COLORS.cyan + "22",
          border: `1px solid ${COLORS.cyan}55`,
          color: COLORS.cyan,
        }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: COLORS.cyan, animation: "pulse 1.5s ease-in-out infinite" }} />
          <span>
            {reEnrichInflight} ingestion job{reEnrichInflight === 1 ? "" : "s"} in flight
          </span>
          <span style={{ color: COLORS.textDim, fontWeight: 400 }}>
            · See Jobs tab for details
          </span>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        padding: "8px 12px", borderRadius: 10,
        border: `1px solid ${COLORS.border}`, background: COLORS.surface,
        display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center",
      }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") reload(); }}
          placeholder="Search by filename or title…"
          style={{
            flex: 1, minWidth: 220, padding: "6px 10px", borderRadius: 8,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12,
          }} />
        <label style={{ fontSize: 11, color: COLORS.textDim, display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={includeDeleted} onChange={e => setIncludeDeleted(e.target.checked)} />
          Show soft-deleted
        </label>
        <button onClick={reload} disabled={loading} style={{
          padding: "6px 14px", borderRadius: 8, border: "none",
          background: COLORS.accent, color: "#fff", cursor: loading ? "wait" : "pointer", fontSize: 12,
        }}>{loading ? "Loading…" : "Refresh"}</button>
        {/* Re-enrich all — admin only. Backend re-checks role + cost_acknowledged. */}
        {isAdmin && (
          <button onClick={() => setReEnrichOpen(true)} disabled={loading || allRows.length === 0} style={{
            padding: "6px 14px", borderRadius: 8,
            border: `1px solid ${COLORS.violet}`, background: "transparent",
            color: COLORS.violet,
            cursor: (loading || allRows.length === 0) ? "not-allowed" : "pointer",
            fontSize: 12,
          }}>Re-enrich all…</button>
        )}
      </div>

      {/* Multi-select header — only shown when developer+ and at least one row */}
      {isDeveloper && allRows.length > 0 && (
        <div style={{
          display: "flex",
          gap: 12, alignItems: "center",
          padding: "4px 12px",
          fontSize: 11, color: COLORS.textDim,
        }}>
          <input
            type="checkbox"
            checked={allVisibleSelected}
            ref={(el) => {
              // Set HTML "indeterminate" attribute imperatively — React
              // doesn't have a JSX prop for it. Visual: partial-check tick.
              if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
            }}
            onChange={toggleAllVisible}
            aria-label="Select all visible documents"
            title={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
          />
          <div style={{ flex: 1 }}>
            {selectedDocs.size > 0
              ? `${selectedDocs.size} selected`
              : "Select rows for bulk actions"}
          </div>
          {selectedDocs.size > 0 && (
            <>
              {/* Bulk delete — only in live view. Restore in trash view (mirror UX). */}
              {!includeDeleted && (
                <button
                  onClick={handleBulkSoftDelete}
                  disabled={bulkBusy}
                  style={{
                    background: bulkBusy ? COLORS.surfaceHover : COLORS.pink,
                    color: bulkBusy ? COLORS.textDim : "#fff",
                    border: "none", padding: "5px 12px", borderRadius: 6,
                    fontSize: 11, fontWeight: 600,
                    cursor: bulkBusy ? "wait" : "pointer",
                  }}
                >
                  {bulkBusy ? "Deleting…" : `Delete selected (${selectedDocs.size})`}
                </button>
              )}
              {includeDeleted && (
                <button
                  onClick={handleBulkRestore}
                  disabled={bulkBusy}
                  style={{
                    background: bulkBusy ? COLORS.surfaceHover : COLORS.green,
                    color: bulkBusy ? COLORS.textDim : "#fff",
                    border: "none", padding: "5px 12px", borderRadius: 6,
                    fontSize: 11, fontWeight: 600,
                    cursor: bulkBusy ? "wait" : "pointer",
                  }}
                >
                  {bulkBusy ? "Restoring…" : `Restore selected (${selectedDocs.size})`}
                </button>
              )}
              <button
                onClick={clearSelection}
                disabled={bulkBusy}
                style={{
                  background: "transparent", color: COLORS.textDim,
                  border: `1px solid ${COLORS.border}`, padding: "5px 10px",
                  borderRadius: 6, fontSize: 11, cursor: "pointer",
                }}
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}

      {/* Rows */}
      {allRows.length === 0 && !loading ? (
        <div style={{ color: COLORS.textDim, fontSize: 12, padding: 18 }}>
          No documents in this agent's knowledge base. Use the Upload sub-tab to ingest some.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {allRows.map(r => {
            const deleted = !!r.deleted_at;
            const menuOpen = openMenu === r.doc_id;
            const busy = busyRow === r.doc_id;
            const checked = selectedDocs.has(r.doc_id);
            return (
              <div key={r.id}
                onClick={() => onOpenDrawer && onOpenDrawer({ kind: `Document: ${r.filename}`, payload: r })}
                style={{
                  padding: "10px 12px", borderRadius: 8,
                  border: `1px solid ${checked ? COLORS.accent : (deleted ? COLORS.orange + "55" : COLORS.border)}`,
                  background: checked ? COLORS.accentSoft : COLORS.surface,
                  cursor: "pointer",
                  display: "grid",
                  gridTemplateColumns: isDeveloper
                    ? "20px 1fr 100px 80px 90px 130px 36px"
                    : "1fr 100px 80px 90px 130px 36px",
                  gap: 8, alignItems: "center", position: "relative",
                  // Rows share position:relative + z-index:auto, so they're
                  // all painted at the same stacking level — later DOM
                  // siblings (rows below) paint on top regardless of the
                  // menu's own z-index, since the menu can't escape its
                  // row's paint slot unless the row establishes its own
                  // stacking context. Promoting the OPEN row's z-index
                  // does that, so its dropdown clears every row below it.
                  zIndex: menuOpen ? 30 : "auto",
                  opacity: deleted ? 0.7 : 1,
                }}
              >
                {isDeveloper && (
                  <div onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleDoc(r.doc_id)}
                      aria-label={`Select ${r.filename}`}
                    />
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: COLORS.text,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{r.filename}</div>
                  {r.title && r.title !== r.filename && (
                    <div style={{ fontSize: 11, color: COLORS.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.title}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
                  {r.doc_type || r.source_type || "—"}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textDim }}>
                  {r.chunk_count} chunk{r.chunk_count === 1 ? "" : "s"}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textDim }}>
                  {r.page_count ? `${r.page_count} page${r.page_count === 1 ? "" : "s"}` : ""}
                </span>
                <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
                  {fmt(r.ingested_at)}
                </span>
                <div onClick={e => e.stopPropagation()} style={{ position: "relative" }}>
                  <button onClick={() => setOpenMenu(menuOpen ? null : r.doc_id)} disabled={busy} style={{
                    background: "transparent", border: `1px solid ${COLORS.border}`,
                    color: COLORS.textDim, padding: "2px 8px", borderRadius: 6,
                    cursor: busy ? "wait" : "pointer", fontSize: 14,
                  }}>{busy ? "…" : "⋮"}</button>
                  {menuOpen && (
                    <div style={{
                      position: "absolute", right: 0, top: "100%", marginTop: 4,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      borderRadius: 8, padding: 4, minWidth: 180, zIndex: 20,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                      display: "flex", flexDirection: "column",
                    }}>
                      <MenuButton onClick={() => { onOpenDrawer && onOpenDrawer({ kind: `Document: ${r.filename}`, payload: r }); setOpenMenu(null); }}>View details</MenuButton>
                      {/* B19 — mutating actions gated to developer+; admin-only for hard delete. */}
                      {isDeveloper && !deleted && <MenuButton onClick={() => onReEnrich(r)}>Re-enrich</MenuButton>}
                      {isDeveloper && !deleted && <MenuButton onClick={() => onSoftDelete(r)}>Soft-delete</MenuButton>}
                      {isDeveloper && deleted && <MenuButton onClick={() => onRestore(r)}>Restore</MenuButton>}
                      {isAdmin && <MenuButton danger onClick={() => onHardDelete(r)}>Delete permanently</MenuButton>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {nextCursor && (
        <button onClick={loadMore} disabled={loading} style={{
          alignSelf: "flex-start",
          padding: "6px 14px", borderRadius: 8,
          border: `1px solid ${COLORS.border}`, background: "transparent",
          color: COLORS.text, cursor: loading ? "wait" : "pointer", fontSize: 12,
        }}>{loading ? "Loading…" : "Load more"}</button>
      )}

      {/* Re-enrich-all modal (Commit 7). admin-gated already at the trigger button. */}
      {reEnrichOpen && (
        <div
          onClick={closeReEnrichModal}
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              borderRadius: 12, padding: 20,
              minWidth: 380, maxWidth: 520,
              display: "flex", flexDirection: "column", gap: 14,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
              Re-enrich all documents
            </div>
            <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.5 }}>
              Re-runs selected ingestion stages on this agent's knowledge base.
              Use this after upgrading the domain template, changing chunk size,
              or switching embedding models. Each document re-processes through
              the engine queue. Cost scales with corpus size + LLM provider.
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.06em" }}>Stages</div>
              {RE_ENRICH_STAGE_OPTIONS.map(opt => (
                <label key={opt.id} style={{ fontSize: 12, color: COLORS.text, display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={reEnrichStages.includes(opt.id)}
                    onChange={() => toggleReEnrichStage(opt.id)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            <label style={{ fontSize: 12, color: COLORS.text, display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={reEnrichOnlyFailed}
                onChange={e => setReEnrichOnlyFailed(e.target.checked)}
              />
              Only re-process documents that previously failed
            </label>

            <label style={{
              fontSize: 11, color: COLORS.text, display: "flex", gap: 8, alignItems: "flex-start",
              padding: 10, borderRadius: 8,
              background: COLORS.surface, border: `1px solid ${COLORS.violet}55`,
            }}>
              <input
                type="checkbox"
                checked={reEnrichCostAck}
                onChange={e => setReEnrichCostAck(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                I acknowledge this will re-process documents through the configured
                LLM provider and may incur material costs.
              </span>
            </label>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <button
                onClick={closeReEnrichModal}
                disabled={reEnrichBusy}
                style={{
                  background: "transparent", color: COLORS.textDim,
                  border: `1px solid ${COLORS.border}`, padding: "6px 14px",
                  borderRadius: 8, fontSize: 12, cursor: reEnrichBusy ? "wait" : "pointer",
                }}
              >Cancel</button>
              <button
                onClick={submitReEnrichAll}
                disabled={reEnrichBusy || reEnrichStages.length === 0 || !reEnrichCostAck}
                style={{
                  background: (reEnrichBusy || reEnrichStages.length === 0 || !reEnrichCostAck) ? COLORS.surfaceHover : COLORS.violet,
                  color: (reEnrichBusy || reEnrichStages.length === 0 || !reEnrichCostAck) ? COLORS.textDim : "#fff",
                  border: "none", padding: "6px 14px",
                  borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: reEnrichBusy ? "wait" : (reEnrichStages.length === 0 || !reEnrichCostAck ? "not-allowed" : "pointer"),
                }}
              >{reEnrichBusy ? "Queueing…" : "Re-enrich all"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuButton({ children, onClick, danger }) {
  return (
    <button onClick={onClick} style={{
      background: "transparent", border: "none",
      color: danger ? COLORS.pink : COLORS.text,
      textAlign: "left", padding: "6px 10px", borderRadius: 6,
      fontSize: 12, cursor: "pointer",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = COLORS.surfaceHover; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >{children}</button>
  );
}
